import './transmission-logs-page.css';
import { useEffect, useState } from 'react';

import _isUndefined from 'lodash/isUndefined';

import { Button, DatePicker, Dropdown, Modal, Select, Space, Table, Tag, notification } from 'antd';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { AiOutlineDelete, AiOutlineFile, AiOutlineRedo } from 'react-icons/ai';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { addControlStyle } from './transmission-logs-page-style';
import { highlight, languages } from "prismjs";
import { saveAs } from "file-saver";

import { TransmissionLog } from '../../model/transmission-log-model';

import {
    useDeleteTransmissionLogMutation,
    useLazyExportTransmissionLogsQuery,
    useLazyGetTransmissionLogQuery,
    useLazyGetTransmissionLogsQuery,
    useResendAllTransmissionLogMutation,
    useResendTransmissionLogMutation
} from '../../redux/api/transmission-logs';

import { Client } from '../../model/client-model';
import Editor from 'react-simple-code-editor';
import { useLazyGetClientsQuery } from '../../redux/api/clients';

const statusOptions = [
    {label: 'Success', value: 'SUCCESS'},
    {label: 'Failed', value: 'FAIL'}
]

const TransmissionLogsPage = () => {

    const viewMenu = {
        key: 'view',
        icon: <AiOutlineFile />,
        label: 'View Transmission Log'
    };

    const resendMenu = {
        key: 'resend',
        icon: <AiOutlineRedo />,
        label: 'Resend Transmission Log'
    };

    const deleteMenu = {
        key: 'delete',
        icon: <AiOutlineDelete />,
        label: 'Delete Transmission'
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 96,
        },
        {
            title: 'Date',
            dataIndex: 'datetime',
            key: 'datetime',
            width: 200,
            render: (datetime: string) => {
                return new Date(datetime + "Z").toLocaleString();
            }
            // sorter: (a: string, b: string) => a.localeCompare(b)
        },
        {
            title: 'Client',
            dataIndex: 'client',
            key: 'client',
            width: 240,
            render: (client: Client) => {
                return client.name;
            }
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 120,
            alignment: 'center',
            render: (status: string) => {
                return status === 'SUCCESS' ? <Tag color="green">SUCCESS</Tag> : <Tag color="red">FAIL</Tag>
            }
        },
        {
            title: 'Retransmitted',
            dataIndex: 'retransmitted',
            key: 'retransmitted',
            width: 160,
            alignment: 'center',
            render: (retransmitted: string) => {
                switch (retransmitted) {
                    case 'NOT_NEEDED': return <Tag color="green">NOT_NEEDED</Tag>
                    case 'YES': return <Tag color="green">YES</Tag>
                    case 'FAILED_YES': return <Tag color="orange">YES BUT FAILED</Tag>
                    default: return <Tag color="red">NO</Tag>
                }
            }
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            render: (description: string) => {
                return description;
            }
        },
        {
            title: 'Action',
            dataIndex: 'action',
            key: 'action',
            width: 120,
            render: (_: any, record: TransmissionLog) => {
                return <div style={{ textAlign: 'center' }}>
                    <Dropdown
                        trigger={['click']}
                        menu={{
                            items: getMenuItems(record?.status),
                            onClick: ({ key }) => handleTransmissionLogAction(key, record)
                        }}>
                        <div style={{ cursor: 'pointer' }} onClick={(e) => e.preventDefault()}>
                            <Space>
                                <span className="dropdown-title">
                                    <BsThreeDotsVertical />
                                </span>
                            </Space>
                        </div>
                    </Dropdown>
                </div>
            }
        },
    ];

    const [ isViewOpen, setViewOpen ] = useState<boolean>(false);
    const [ transmissionLog, setTransmissionLog ] = useState<string>('');
    const [ transmissionLogs, setTransmissionLogs ] = useState<TransmissionLog[]>([]);
    const [ tableHeight, setTableHeight ] = useState(0);
    const [ clientId, setClientId ] = useState<number | null>(null);
    const [ status, setStatus ] = useState<string | null>(null);
    const [ startDate, setStartDate ] = useState<string | null>(null);
    const [ endDate, setEndDate ] = useState<string | null>(null);
    const [ clientOptions, setClientOptions ] = useState<any>([]);
    const [ page, setPage ] = useState<any>({
        current: 1,
        pageSize: 25,
        total: 0,
    });

    const [modal, contextHolder] = Modal.useModal();

    const [ getTransmissionLog] = useLazyGetTransmissionLogQuery();
    const [ getTransmissionLogs] = useLazyGetTransmissionLogsQuery();
    const [ resendTransmissionLog] = useResendTransmissionLogMutation();
    const [ resendAllTransmissionLog] = useResendAllTransmissionLogMutation();
    const [ removeTransmissionLog] = useDeleteTransmissionLogMutation();
    const [ getClients ] = useLazyGetClientsQuery();
    const [ exportTransmissionLogs ] = useLazyExportTransmissionLogsQuery();

    const updateTableHeight = () => {
        const windowHeight = window.innerHeight; // Full screen height
        const calculatedHeight = windowHeight - 288; // Subtract paddings and headers
        setTableHeight(calculatedHeight);
    };

    useEffect(() => {

        updateTableHeight(); // Set height on mount
        fetchClients();
        window.addEventListener("resize", updateTableHeight); // Adjust height on resize
        return () => window.removeEventListener("resize", updateTableHeight);

    }, []);

    useEffect(() => {
        fetchTransmissionLogs(clientId, status, startDate, endDate);
    }, [getTransmissionLogs]);

    useEffect(() => {
        fetchTransmissionLogs(clientId, status, startDate, endDate);
    }, [page.current]);

    const fetchClients = async () => {
        const clients = await getClients({current: 1, viewableSize: 1000, total: 0}).unwrap();
        setClientOptions(clients.items.map((client: any) => {
            return {
                value: client.id,
                label: client.name
            }
        }));
    }

    const fetchTransmissionLogs = async (clientId: number | null, status: string | null, startDate: string | null, endDate: string | null) => {
        
        let params: any = { index: page.current, viewableSize: page.pageSize };
        if (clientId) params = {...params, clientId};
        if (status) params = {...params, status};
        if (startDate) params = {...params, startDate};
        if (endDate) params = {...params, endDate};
        
        const logs = await getTransmissionLogs(params).unwrap();
        const indexedTransmissionLogs = logs.items.map((log: TransmissionLog, index: number) => {
            return { ...log, key: index };
        });
        setTransmissionLogs(indexedTransmissionLogs);
        setPage({ ...page, viewableSize: logs.page.viewableSize, total: logs.page.totalSize });
    }

    const changeClientId = (id: number) => {
        setClientId(id);
        fetchTransmissionLogs(id, status, startDate, endDate);
    }

    const changeStatus = (status: string) => {
        setStatus(status);
        fetchTransmissionLogs(clientId, status, startDate, endDate);
    }

    const changeStartDate = (e: any) => {
        const date = e.format('YYYY-MM-DD');
        fetchTransmissionLogs(clientId, status, date, endDate);
        setStartDate(date);
    }

    const changeEndDate = (e: any) => {
        const date = e.format('YYYY-MM-DD');
        fetchTransmissionLogs(clientId, status, startDate, date);
        setEndDate(date);
    }

    /**
     * Gets the menu items available to the client depending on its current state
     * 
     * @param scheduled if scheduled
     * @param payloadType the payload type (XML, JSON, PDF, etc)
     * @returns the menu items
     */
    const getMenuItems = (status?: string) => {
        return [
            viewMenu,
            resendMenu,
            deleteMenu,
        ];
    }

    const fetchTransmissionLog = async (id?: number) => {
        const log = await getTransmissionLog(id).unwrap();
        setTransmissionLog(log.data);
        setViewOpen(true);
    }

    const deleteTransmissionLog = async (log: TransmissionLog | undefined): Promise<void> => {
        try {

            await removeTransmissionLog(log?.id).unwrap();
            await fetchTransmissionLogs(clientId, status, startDate, endDate);

            notification.success({
                message: "Transmission Log Deleted!",
                description: `The transmision log has been deleted.`
            });

        } catch (e) {
            console.log(e);
            notification.error({
                message: "Deleting Transmission Log Failed!",
                description: `An error occurred while deleting the transmission log.`
            });
        }
    }

    /**
     * Retransmits a failed transmission.
     */
    const retransmitTransmissionLog = async (transmissionLog: TransmissionLog) => {

        try {

            const response = await resendTransmissionLog(transmissionLog?.id).unwrap();
            const index = transmissionLogs?.findIndex((log: TransmissionLog) => log.id === response.id);

            let newTransmissionLogs: TransmissionLog[] = [...transmissionLogs];
            newTransmissionLogs[index] = response;
            setTransmissionLogs(newTransmissionLogs);

            notification.success({
                message: "Data Transmitted!",
                description: `The data has been resent.`
            });
        } catch (e) {
            notification.error({
                message: "Resend Transmission Log Failed!",
                description: `An error occurred while transmitting.`
            });
        }
    }

    /**
     * Retransmits all failed transmission logs.
     */
    const retransmitAllTransmissionLog = async () => {

        try {
            await resendAllTransmissionLog(null).unwrap();
            await fetchTransmissionLogs(clientId, status, startDate, endDate);

            notification.success({
                message: "Data Transmitted!",
                description: `The data has been resent.`
            });
        } catch (e) {
            console.log(e);
            notification.error({
                message: "Resend Transmission Log Failed!",
                description: `An error occurred while transmitting.`
            });
        }
    }

    /**
     * Retransmits all failed transmission logs.
     */
    const exportLogs = async () => {

        try {

            let params: any = { index: page.current, viewableSize: page.pageSize };
            if (clientId) params = {...params, clientId};
            if (status) params = {...params, status};
            if (startDate) params = {...params, startDate};
            if (endDate) params = {...params, endDate};
            
            const report = await exportTransmissionLogs(params).unwrap();
            const blob = new Blob([report], { type: 'text/csv;charset=utf-8;' });
            saveAs(blob, "transmission-logs.csv");
            
            notification.success({
                message: "Logs Exported!",
                description: `The data has been exported.`
            });
        } catch (e) {
            console.log(e);
            notification.error({
                message: "Export Logs Failed!",
                description: `An error occurred while exporting the data.`
            });
        }
    }

    /**
     * Confirms if request to delete is valid, then proceeds to
     * delete if confirmed.
     */
    const onResend = (log: TransmissionLog) => {

        modal.confirm({
            title: 'Resend Failed Transmission',
            icon: <ExclamationCircleOutlined />,
            content: 'Are you sure to resend this data?',
            okText: 'Ok',
            cancelText: 'Cancel',
            onOk: () => retransmitTransmissionLog(log),
        });
    };

    /**
     * Confirms if request to delete is valid, then proceeds to
     * delete if confirmed.
     */
    const onResendAll = () => {

        modal.confirm({
            title: 'Resend All Failed Transmissions',
            icon: <ExclamationCircleOutlined />,
            content: 'Are you sure you want to resend all failed data?',
            okText: 'Ok',
            cancelText: 'Cancel',
            onOk: () => retransmitAllTransmissionLog(),
        });
    };

    /**
     * Confirms if request to delete is valid, then proceeds to
     * delete if confirmed.
     */
    const onExport = () => {

        modal.confirm({
            title: 'Export Logs',
            icon: <ExclamationCircleOutlined />,
            content: 'Are you sure you want to export the transmission logs?',
            okText: 'Ok',
            cancelText: 'Cancel',
            onOk: () => exportLogs(),
        });
    };

    /**
     * Confirms if request to delete is valid, then proceeds to
     * delete if confirmed.
     */
    const onDelete = (transmissionLog: TransmissionLog) => {

        modal.confirm({
            title: 'Delete Log',
            icon: <ExclamationCircleOutlined />,
            content: 'Are you sure to delete this log?',
            okText: 'Ok',
            cancelText: 'Cancel',
            onOk: () => deleteTransmissionLog(transmissionLog),
        });
    };

    /**
     * Handles the click action on the action button of the client
     * in the client list (the 3-dot vertical button on the right-most
     * column)
     * 
     * @param key the key of the menu item that was clicked
     * @param client the client that was clicked
     */

    const handleTransmissionLogAction = async (key: string, log: TransmissionLog) => {
        switch (key) {
            case 'view':
                await fetchTransmissionLog(log?.id);
                break;
            case 'resend': onResend(log); break;
            case 'delete': onDelete(log); break;
        }
    }

    // Handle page change
    const handleTableChange = (page: any) => {
        setPage((prev: any) => ({ ...prev, current: page.current }));
    }

    return <>
        {contextHolder}
        <div style={addControlStyle}>
            <h1>Transmission Logs</h1>
            <div>
                <Select value={clientId} onChange={changeClientId} style={{width: '12rem'}} options={clientOptions} placeholder="Select client"/>
                &nbsp;
                <Select value={status} onChange={changeStatus} style={{width: '8rem'}} options={statusOptions} placeholder="Select status"/>
                &nbsp;
                <DatePicker onChange={changeStartDate} placeholder="Select start date"/>
                &nbsp;
                <DatePicker onChange={changeEndDate} placeholder="Select end date"/>
                &nbsp;
                <Button
                    type="primary"
                    onClick={onResendAll}
                >
                    Resend All Failed Logs
                </Button>
                &nbsp;
                <Button
                    type="primary"
                    onClick={onExport}
                >
                    Export Logs
                </Button>
            </div>

        </div>

        <Table
            dataSource={transmissionLogs}
            columns={columns}
            scroll={{ y: tableHeight }}
            pagination={{
                current: page.current,
                pageSize: page.pageSize,
                total: page.total
            }}
            onChange={handleTableChange} />

        <Modal
            title={'Logged Data'}
            closable={false}
            open={isViewOpen}
            onOk={() => setViewOpen(false)}
            cancelButtonProps={{ style: { display: 'none' } }}
            width={600}
        >
            <Editor
                value={transmissionLog}
                highlight={(code) => highlight(code, languages.js!, 'js')}
                padding={10}
                contentEditable={false}
                onValueChange={() => { }}
                style={{
                    minHeight: '25rem',
                    background: 'white',
                    border: 'none',
                    fontFamily: '"Fira code", "Fira Mono", monospace',
                    fontSize: 14,
                }}
            />
        </Modal>
    </>
};

export default TransmissionLogsPage;

